<template>
  <div class="blueprint-editor" :class="{ 'blueprint-editor--modal': isModal }">
    <!-- Breadcrumb & Top Bar (Page mode) -->
    <div v-if="!isModal" class="ms-page-header">
      <div class="ms-page-header__content">
        <div class="ms-page-header__icon">
          <i class="fa-solid fa-diagram-project"></i>
        </div>
        <div>
          <p class="ms-page-header__breadcrumb">
            Platform Settings <span class="ms-breadcrumb-sep">/</span>
            <router-link to="/admin/jobs-catalogue" class="text-decoration-none">Jobs Catalogue</router-link>
            <span class="ms-breadcrumb-sep">/</span>
            <span>{{ isEditMode ? 'Edit Blueprint' : 'New Blueprint' }}</span>
          </p>
          <h1 class="ms-page-header__title">
            {{ isEditMode ? (form.title || 'Edit Job Blueprint') : 'Create New Job Blueprint' }}
          </h1>
          <p class="ms-page-header__subtitle">
            Define the delivery workflow hierarchy, automation pipelines, and assignment policies for this service.
          </p>
        </div>
      </div>
    </div>

    <!-- Modal Header (Modal mode) -->
    <div v-if="isModal" class="pp-editor-modal-header">
      <div class="d-flex align-items-center gap-3">
        <div class="pp-editor-icon">
          <i class="fa-solid fa-diagram-project"></i>
        </div>
        <div>
          <h4 class="pp-editor-title">
            {{ isCloneMode ? 'Duplicate Job Blueprint' : isEditMode ? 'Edit Job Blueprint' : 'Create Job Blueprint' }}
          </h4>
          <p class="pp-editor-subtitle">
            Configure blueprint tasks, dynamic role assignments, and integration targets.
          </p>
        </div>
      </div>
      <button type="button" class="pp-editor-close" @click="handleCancel">&times;</button>
    </div>

    <!-- Main Editor Content Area -->
    <div class="pp-editor-body">
      <!-- Loading State -->
      <div v-if="loading" class="text-center py-5">
        <i class="fas fa-circle-notch fa-spin fa-2x text-teal mb-2"></i>
        <p class="text-muted">Loading blueprint specifications...</p>
      </div>

      <div v-else>
        <!-- ── Section 1: Target Product & Metadata Card ── -->
        <div class="pp-section-card mb-4">
          <div class="pp-section-header">
            <h6 class="pp-section-title">
              <i class="fas fa-box-open me-2 text-teal"></i> Product / Service Association & Metadata
            </h6>
            <span class="badge bg-primary-subtle text-primary fw-semibold small">Step 1</span>
          </div>

          <div class="pp-section-content">
            <div class="row g-3">
              <!-- Target Product Selector -->
              <div class="col-12 col-md-6">
                <label class="pp-form-label required">Target Product / Service</label>
                <select
                  v-model="form.product_id"
                  class="pp-form-select"
                  :class="{ 'is-invalid': submitted && !form.product_id, 'bg-light': isProductLocked }"
                  :disabled="isProductLocked"
                  @change="handleProductSelected"
                >
                  <option value="">-- Select Product / Service --</option>
                  <option
                    v-for="prod in availableProducts"
                    :key="prod.product_id"
                    :value="prod.product_id"
                  >
                    {{ prod.display_name || prod.product_name }}
                    {{ getAuthItemCode(prod) ? `(${getAuthItemCode(prod)})` : '' }}
                    {{ prod.blueprints_count ? ` - [${prod.blueprints_count} Blueprint(s)]` : (prod.has_blueprint ? ' - [Configured]' : '') }}
                  </option>
                </select>
                <div v-if="isProductLocked && !isEditMode" class="text-muted small mt-1">
                  <i class="fas fa-lock me-1 text-teal"></i> Locked to selected product
                </div>
                <div v-if="submitted && !form.product_id" class="text-danger small mt-1">
                  Please select a product or service.
                </div>
              </div>

              <!-- Auth Item Code -->
              <div class="col-12 col-md-3">
                <label class="pp-form-label">Auth Item Code</label>
                <input
                  v-model="form.auth_item_code"
                  type="text"
                  class="pp-form-input bg-light"
                  placeholder="Auto-synced from Auth"
                  readonly
                />
              </div>

              <!-- Active Status Toggle -->
              <div class="col-12 col-md-3 d-flex flex-column justify-content-center">
                <label class="pp-form-label">Blueprint Status</label>
                <label class="pp-toggle-switch mt-1">
                  <input v-model="form.is_active" type="checkbox" />
                  <span class="pp-toggle-slider"></span>
                  <span class="pp-toggle-label">{{ form.is_active ? 'Active' : 'Inactive' }}</span>
                </label>
              </div>

              <!-- Blueprint Title -->
              <div class="col-12 col-md-6">
                <label class="pp-form-label required">Blueprint Title</label>
                <input
                  v-model.trim="form.title"
                  type="text"
                  class="pp-form-input"
                  :class="{ 'is-invalid': submitted && !form.title }"
                  placeholder="e.g. Standard Penetration Test Delivery Workflow"
                />
                <div v-if="submitted && !form.title" class="text-danger small mt-1">
                  Blueprint Title is required.
                </div>
              </div>

              <!-- Blueprint Description -->
              <div class="col-12 col-md-6">
                <label class="pp-form-label">Workflow Scope / Description</label>
                <input
                  v-model.trim="form.description"
                  type="text"
                  class="pp-form-input"
                  placeholder="Optional brief description of this workflow"
                />
              </div>
            </div>
          </div>
        </div>

        <!-- ── Section 2: Hierarchical Task Tree Builder ── -->
        <div class="pp-section-card mb-4">
          <div class="pp-section-header">
            <div>
              <h6 class="pp-section-title">
                <i class="fas fa-sitemap me-2 text-teal"></i> Hierarchical Task Tree
              </h6>
              <p class="pp-section-subtitle mb-0">
                Define the sequenced tasks, sub-tasks, execution rules, and dynamic roles executed when a Lead converts.
              </p>
            </div>
            <button type="button" class="pp-btn pp-btn--primary pp-btn--sm" @click="addTask">
              <i class="fas fa-plus"></i> Add Task
            </button>
          </div>

          <div class="pp-section-content">
            <!-- Validation Summary Alert -->
            <div v-if="submitted && validationErrors.length > 0" class="pp-alert-danger mb-3">
              <div class="d-flex align-items-center gap-2 mb-1 fw-bold">
                <i class="fas fa-exclamation-triangle"></i> Please resolve the following issues:
              </div>
              <ul class="mb-0 ps-3 small">
                <li v-for="(err, i) in validationErrors" :key="i">{{ err }}</li>
              </ul>
            </div>

            <!-- Empty Tree State -->
            <div v-if="form.tasks.length === 0" class="pp-tree-empty-state">
              <div class="pp-empty-icon-wrap">
                <i class="fas fa-diagram-project fa-2x text-muted"></i>
              </div>
              <h6 class="fw-bold mt-3 mb-1">No tasks defined yet</h6>
              <p class="text-muted small max-w-500 mx-auto">
                Every job blueprint requires an ordered workflow of tasks and nested sub-tasks. Start by adding your first task.
              </p>
              <button type="button" class="pp-btn pp-btn--primary" @click="addTask">
                <i class="fas fa-plus me-1"></i> Add First Task
              </button>
            </div>

            <!-- Tree of Tasks -->
            <div v-else class="tree-root-container">
              <TaskTreeNode
                v-for="(task, idx) in form.tasks"
                :key="task.id || idx"
                ref="treeNodes"
                :task="task"
                :index="idx"
                :depth="0"
                :total-siblings="form.tasks.length"
                :hierarchy-levels="lookupValues.hierarchy_levels"
                :execution-modes="lookupValues.execution_modes"
                :assignment-types="lookupValues.assignment_types"
                :dynamic-roles="lookupValues.dynamic_roles"
                :frequencies="lookupValues.frequencies"
                :integration-targets="lookupValues.integration_targets"
                :internal-users="internalUsers"
                :submitted="submitted"
                @add-child="handleAddChild"
                @remove-task="handleRemoveTask"
                @move-up="handleMoveUp"
                @move-down="handleMoveDown"
                @update-field="handleTaskFieldUpdate"
                @update:task="handleTaskUpdate(idx, $event)"
              />
            </div>

            <!-- Bottom Add Button -->
            <div v-if="form.tasks.length > 0" class="mt-3 text-center">
              <button type="button" class="pp-btn pp-btn--ghost pp-btn--sm" @click="addTask">
                <i class="fas fa-plus me-1"></i> Add Another Task
              </button>
            </div>
          </div>

          <!-- Task Tree Summary Footer -->
          <div class="pp-tree-summary-footer">
            <div class="d-flex align-items-center gap-3 text-muted small flex-wrap">
              <span><strong>Total Tasks:</strong> {{ totalTasksCount }}</span>
              <span>&bull;</span>
              <span><strong>Tasks:</strong> {{ form.tasks.length }}</span>
              <span>&bull;</span>
              <span><strong>Sequential:</strong> {{ sequentialCount }}</span>
              <span>&bull;</span>
              <span><strong>Concurrent:</strong> {{ concurrentCount }}</span>
            </div>

            <div class="d-flex align-items-center gap-2">
              <button type="button" class="pp-btn pp-btn--ghost" @click="handleCancel">Cancel</button>
              <button
                type="button"
                class="pp-btn pp-btn--primary px-4"
                :disabled="saving"
                @click="handleSubmit"
              >
                <span v-if="saving" class="spinner-border spinner-border-sm me-1" role="status"></span>
                <i v-else class="fas fa-check me-1"></i>
                Save & Publish Blueprint
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import TaskTreeNode from "./TaskTreeNode.vue";

export default {
  name: "JobBlueprintEditor",
  components: {
    TaskTreeNode,
  },
  props: {
    blueprintId: {
      type: String,
      default: "",
    },
    initialProductId: {
      type: String,
      default: "",
    },
    cloneFromId: {
      type: String,
      default: "",
    },
    isModal: {
      type: Boolean,
      default: false,
    },
  },
  emits: ["saved", "close"],
  data() {
    return {
      loading: false,
      saving: false,
      submitted: false,

      availableProducts: [],
      internalUsers: [],
      lookupValues: {
        hierarchy_levels: ["Task", "Sub-task"],
        execution_modes: ["Sequential", "Concurrent"],
        assignment_types: ["Dynamic (Role-Based)", "Static (Specific User)"],
        dynamic_roles: ["Vertex Organisation Lead", "BDM", "Super Admin"],
        frequencies: [
          "One-off", "Daily", "Weekly", "Fortnightly", "Monthly",
          "Quarterly", "Biannually", "Annually", "Every 2 Years",
          "Every 3 Years", "Every 4 Years", "Every 5 Years",
        ],
        integration_targets: [
          "None", "Tallyfy", "Pentester", "Alke", "SOC",
          "Confluence", "Google Forms", "Custom Webhook",
        ],
      },

      form: {
        product_id: "",
        auth_item_code: "",
        title: "",
        description: "",
        is_active: true,
        tasks: [],
      },
    };
  },
  computed: {
    effectiveId() {
      return this.blueprintId || this.$route.params.id || "";
    },
    isEditMode() {
      return !!this.effectiveId && !this.cloneFromId;
    },
    isCloneMode() {
      return !!this.cloneFromId;
    },
    isProductLocked() {
      if (this.isEditMode && !this.isCloneMode) return true;
      const initialProd = this.initialProductId || this.$route.query?.product_id;
      if (initialProd && !this.isCloneMode) return true;
      return false;
    },
    totalTasksCount() {
      let count = 0;
      const countTasks = (arr) => {
        arr.forEach((t) => {
          count++;
          if (t.sub_tasks && t.sub_tasks.length > 0) {
            countTasks(t.sub_tasks);
          }
        });
      };
      countTasks(this.form.tasks);
      return count;
    },
    sequentialCount() {
      let count = 0;
      const countSeq = (arr) => {
        arr.forEach((t) => {
          if (t.execution_mode === "Sequential" || !t.execution_mode) count++;
          if (t.sub_tasks && t.sub_tasks.length > 0) countSeq(t.sub_tasks);
        });
      };
      countSeq(this.form.tasks);
      return count;
    },
    concurrentCount() {
      let count = 0;
      const countConc = (arr) => {
        arr.forEach((t) => {
          if (t.execution_mode === "Concurrent") count++;
          if (t.sub_tasks && t.sub_tasks.length > 0) countConc(t.sub_tasks);
        });
      };
      countConc(this.form.tasks);
      return count;
    },
    validationErrors() {
      const errors = [];
      if (!this.form.title) {
        errors.push("Blueprint Title is required.");
      }
      if (!this.form.product_id) {
        errors.push("Target Product / Service must be selected.");
      }
      if (this.form.tasks.length === 0) {
        errors.push("Blueprint must have at least one task.");
      }

      const validateTask = (task, path) => {
        if (!task.title) {
          errors.push(`Task at ${path} is missing a Title.`);
        }
        if (task.assignment_type === "Static" && !task.static_user_id) {
          errors.push(`Task "${task.title || path}" is set to Static assignment but has no user selected.`);
        }
        if (task.is_approval_gate && !task.approval_role) {
          errors.push(`Task "${task.title || path}" requires an Approver Role when Approval Gate is enabled.`);
        }
        if (task.sub_tasks && task.sub_tasks.length > 0) {
          task.sub_tasks.forEach((sub, idx) => {
            validateTask(sub, `${path} > Subtask #${idx + 1}`);
          });
        }
      };

      this.form.tasks.forEach((t, idx) => {
        validateTask(t, `Task #${idx + 1}`);
      });

      return errors;
    },
  },
  watch: {
    blueprintId(newVal, oldVal) {
      if (newVal !== oldVal) {
        this.init();
      }
    },
    cloneFromId(newVal, oldVal) {
      if (newVal !== oldVal) {
        this.init();
      }
    },
    initialProductId(newVal, oldVal) {
      if (newVal !== oldVal) {
        this.init();
      }
    },
  },
  created() {
    this.checkSuperAdminAccess();
    this.init();
  },
  methods: {
    checkSuperAdminAccess() {
      const isSuperAdmin = this.$store.state.auth.isSuperAdmin;
      const isAdmin = this.$store.state.auth.isAdmin;
      if (!isSuperAdmin && !isAdmin) {
        this.$toast.error("Unauthorized! Only Super Admins and Admins can manage job blueprints.");
        if (!this.isModal) {
          this.$router.push("/dashboard");
        }
      }
    },
    async init() {
      this.loading = true;
      try {
        await Promise.all([
          this.fetchLookupValues(),
          this.fetchProducts(),
          this.fetchInternalUsers(),
        ]);

        if (this.effectiveId) {
          await this.loadBlueprint(this.effectiveId);
        } else if (this.cloneFromId) {
          await this.loadBlueprintForCloning(this.cloneFromId);
        } else {
          const initialProd = this.initialProductId || this.$route.query?.product_id;
          if (initialProd) {
            this.form.product_id = initialProd;
            this.handleProductSelected();
          }
        }
      } catch (err) {
        console.error("Initialization error:", err);
        this.$toast.error("Failed to initialize Blueprint Editor.");
      } finally {
        this.loading = false;
      }
    },
    async fetchLookupValues() {
      try {
        const resp = await this.$axios.get("/api/v1/jobs/blueprints/lookup-values");
        if (resp.data && resp.data.data) {
          this.lookupValues = Object.assign({}, this.lookupValues, resp.data.data);
        }
      } catch (err) {
        console.warn("Could not fetch lookup values, using defaults:", err);
      }
    },
    async fetchProducts() {
      try {
        const resp = await this.$axios.get("/api/v1/jobs/blueprints/products");
        if (resp.data && resp.data.data) {
          this.availableProducts = resp.data.data;
        }
      } catch (err) {
        console.warn("Could not fetch available products for blueprint editor:", err);
      }
    },
    async fetchInternalUsers() {
      try {
        const [userResp, bdmResp] = await Promise.all([
          this.$axios.get("/auth/users/super-admin").catch(() => ({ data: {} })),
          this.$axios.get("/vertex-bdm").catch(() => ({ data: {} })),
        ]);

        const usersList = [];
        const seenIds = new Set();

        // 1. Add BDMs first
        const bdms = (bdmResp.data && (bdmResp.data.bdms || bdmResp.data.data)) || [];
        bdms.forEach((b) => {
          const id = b.auth_user_id || b.db_id || b.id;
          if (id && !seenIds.has(String(id))) {
            seenIds.add(String(id));
            const name = b.name || `${b.first_name || ""} ${b.last_name || ""}`.trim() || b.email;
            usersList.push({
              id: id,
              name: name,
              full_name: `${name} (BDM)`,
              email: b.email,
              role: "BDM",
            });
          }
        });

        // 2. Add Super Admin / internal users
        const superAdmins = (userResp.data && (userResp.data.super_admin_users || userResp.data.data || userResp.data.users)) || [];
        superAdmins.forEach((u) => {
          const id = u.id || u.user_id;
          if (id && !seenIds.has(String(id))) {
            seenIds.add(String(id));
            const name = u.full_name || u.name || `${u.first_name || ""} ${u.last_name || ""}`.trim() || u.email;
            usersList.push({
              id: id,
              name: name,
              full_name: name,
              email: u.email,
              role: u.role || "Admin",
            });
          }
        });

        this.internalUsers = usersList;
      } catch (err) {
        console.warn("Could not fetch internal users list:", err);
      }
    },
    async loadBlueprint(uuid) {
      if (!uuid) return;
      const resp = await this.$axios.get(`/api/v1/jobs/blueprints/${uuid}`);
      if (resp.data && resp.data.data) {
        const bp = resp.data.data;
        const mappedProductId = bp.product_id || (bp.product ? (bp.product.uuid || bp.product.id) : "") || "";
        this.form = {
          product_id: mappedProductId,
          auth_item_code: bp.auth_item_code || "",
          title: bp.title || "",
          description: bp.description || "",
          is_active: bp.is_active !== undefined ? bp.is_active : true,
          tasks: this.sanitizeTasksForForm(bp.tasks || []),
        };
      }
    },
    async loadBlueprintForCloning(uuid) {
      if (!uuid) return;
      const resp = await this.$axios.get(`/api/v1/jobs/blueprints/${uuid}`);
      if (resp.data && resp.data.data) {
        const bp = resp.data.data;
        this.form = {
          product_id: "",
          auth_item_code: "",
          title: `${bp.title} (Clone)`,
          description: bp.description || "",
          is_active: true,
          tasks: this.sanitizeTasksForForm(bp.tasks || [], true),
        };
      }
    },
    sanitizeTasksForForm(tasks, stripIds = false) {
      return tasks.map((t, index) => {
        const clean = {
          id: stripIds ? undefined : t.id,
          uuid: stripIds ? undefined : t.uuid,
          parent_task_id: stripIds ? undefined : t.parent_task_id,
          hierarchy_level: t.hierarchy_level || (t.parent_task_id ? "Sub-task" : "Task"),
          title: t.title || "",
          description: t.description || "",
          sequence_order: t.sequence_order !== undefined ? t.sequence_order : index,
          execution_mode: t.execution_mode || "Sequential",
          frequency: t.frequency || "One-off",
          assignment_type: t.assignment_type || "Dynamic",
          static_user_id: t.static_user_id || "",
          dynamic_role: t.dynamic_role || "Vertex Organisation Lead",
          is_approval_gate: !!t.is_approval_gate,
          approval_role: t.approval_role || "",
          integration_target: t.integration_target || "None",
          payload_data: t.payload_data || "",
          sub_tasks: t.sub_tasks ? this.sanitizeTasksForForm(t.sub_tasks, stripIds) : [],
        };
        return clean;
      });
    },
    handleProductSelected() {
      const selected = this.availableProducts.find((p) => p.product_id === this.form.product_id);
      if (selected) {
        this.form.auth_item_code = this.getAuthItemCode(selected) || selected.auth_item_code || "";
        if (!this.form.title) {
          this.form.title = `${selected.display_name || selected.product_name} Delivery Workflow`;
        }
      }
    },
    createNewTaskNode(hierarchyLevel = "Task") {
      return {
        id: "temp_" + Math.random().toString(36).substring(2, 9),
        hierarchy_level: hierarchyLevel,
        title: "",
        description: "",
        sequence_order: 0,
        execution_mode: "Sequential",
        frequency: "One-off",
        assignment_type: "Dynamic",
        static_user_id: "",
        dynamic_role: "Vertex Organisation Lead",
        is_approval_gate: false,
        approval_role: "",
        integration_target: "None",
        payload_data: "",
        sub_tasks: [],
      };
    },
    addTask() {
      const newTask = this.createNewTaskNode("Task");
      newTask.sequence_order = this.form.tasks.length;
      this.form.tasks.push(newTask);
      this.reindexTasks(this.form.tasks);
    },
    addRootTask() {
      this.addTask();
    },
    handleAddChild({ parentTask, hierarchyLevel }) {
      if (!parentTask.sub_tasks) {
        parentTask.sub_tasks = [];
      }
      const child = this.createNewTaskNode(hierarchyLevel || "Sub-task");
      child.sequence_order = parentTask.sub_tasks.length;
      parentTask.sub_tasks.push(child);
      this.reindexTasks(parentTask.sub_tasks);
    },
    handleRemoveTask(taskToRemove) {
      const removeRecursive = (list) => {
        const index = list.indexOf(taskToRemove);
        if (index > -1) {
          list.splice(index, 1);
          this.reindexTasks(list);
          return true;
        }
        for (const item of list) {
          if (item.sub_tasks && item.sub_tasks.length > 0) {
            if (removeRecursive(item.sub_tasks)) {
              return true;
            }
          }
        }
        return false;
      };
      removeRecursive(this.form.tasks);
    },
    handleMoveUp({ task, parentTask }) {
      const list = parentTask ? parentTask.sub_tasks : this.form.tasks;
      const index = list.indexOf(task);
      if (index > 0) {
        const temp = list[index];
        list.splice(index, 1);
        list.splice(index - 1, 0, temp);
        this.reindexTasks(list);
      }
    },
    handleMoveDown({ task, parentTask }) {
      const list = parentTask ? parentTask.sub_tasks : this.form.tasks;
      const index = list.indexOf(task);
      if (index > -1 && index < list.length - 1) {
        const temp = list[index];
        list.splice(index, 1);
        list.splice(index + 1, 0, temp);
        this.reindexTasks(list);
      }
    },
    handleTaskFieldUpdate({ task, field, value }) {
      task[field] = value;
    },
    handleTaskUpdate(index, updatedTask) {
      this.form.tasks[index] = updatedTask;
    },
    handleRootTaskUpdate(index, updatedTask) {
      this.handleTaskUpdate(index, updatedTask);
    },
    reindexTasks(list) {
      list.forEach((t, i) => {
        t.sequence_order = i;
        if (t.sub_tasks && t.sub_tasks.length > 0) {
          this.reindexTasks(t.sub_tasks);
        }
      });
    },
    async handleSubmit() {
      this.submitted = true;
      if (this.validationErrors.length > 0) {
        this.$toast.error("Please fill in all required fields.");
        return;
      }

      this.saving = true;

      const prepareTaskPayload = (task, index) => ({
        id: task.id && !task.id.startsWith("temp_") ? task.id : undefined,
        uuid: task.uuid || undefined,
        hierarchy_level: task.hierarchy_level || "Task",
        title: task.title,
        description: task.description || "",
        sequence_order: index,
        execution_mode: task.execution_mode || "Sequential",
        frequency: task.frequency || "One-off",
        assignment_type: task.assignment_type || "Dynamic",
        static_user_id:
          task.assignment_type === "Static" &&
          task.static_user_id !== "" &&
          task.static_user_id !== null &&
          task.static_user_id !== undefined &&
          !isNaN(Number(task.static_user_id))
            ? Number(task.static_user_id)
            : null,
        dynamic_role: task.assignment_type === "Dynamic" ? task.dynamic_role : "",
        is_approval_gate: !!task.is_approval_gate,
        approval_role: task.is_approval_gate ? task.approval_role : "",
        integration_target: task.integration_target || "None",
        payload_data: task.integration_target !== "None" ? task.payload_data : "",
        sub_tasks: (task.sub_tasks || []).map((sub, sIdx) => prepareTaskPayload(sub, sIdx)),
      });

      const payload = {
        product_id: this.form.product_id,
        auth_item_code: this.form.auth_item_code,
        title: this.form.title,
        description: this.form.description,
        is_active: this.form.is_active,
        tasks: this.form.tasks.map((t, idx) => prepareTaskPayload(t, idx)),
      };

      try {
        let resp;
        if (this.isEditMode) {
          resp = await this.$axios.put(`/api/v1/jobs/blueprints/${this.effectiveId}`, payload);
          this.$toast.success("Job Blueprint updated successfully!");
        } else {
          resp = await this.$axios.post("/api/v1/jobs/blueprints", payload);
          this.$toast.success("Job Blueprint created successfully!");
        }

        const savedBlueprint = resp.data && resp.data.data ? resp.data.data : null;
        this.$emit("saved", savedBlueprint);

        if (!this.isModal) {
          this.$router.push("/admin/jobs-catalogue");
        }
      } catch (err) {
        console.error("Save blueprint error:", err);
        const errorMsg = err.response && err.response.data && err.response.data.message
          ? err.response.data.message
          : "Failed to save Job Blueprint. Please try again.";
        this.$toast.error(errorMsg);
      } finally {
        this.saving = false;
      }
    },
    handleCancel() {
      if (this.isModal) {
        this.$emit("close");
      } else {
        this.$router.push("/admin/jobs-catalogue");
      }
    },
    isUUID(uuid) {
      if (typeof uuid !== "string") return false;
      if (uuid.length !== 36) return false;
      if (uuid[8] !== "-" || uuid[13] !== "-" || uuid[18] !== "-" || uuid[23] !== "-") return false;
      return true;
    },
    getAuthItemCode(item) {
      if (!item) return "";
      if (item.auth_item_code && !this.isUUID(item.auth_item_code)) {
        return item.auth_item_code;
      }
      if (item.product_id && !this.isUUID(item.product_id)) {
        return item.product_id;
      }
      if (item.id && !this.isUUID(item.id)) {
        return item.id;
      }
      if (item.product_name && !this.isUUID(item.product_name)) {
        return item.product_name;
      }
      if (item.name && !this.isUUID(item.name)) {
        return item.name;
      }
      return "";
    },
  },
};
</script>

<style scoped>
/* ── Page Header Styles ── */
.ms-page-header {
  margin-bottom: 24px;
  padding: 20px 24px;
  background: linear-gradient(135deg, #ffffff 0%, #f4f9fb 100%);
  border: 1px solid rgba(0, 130, 152, 0.15);
  border-left: 5px solid #008298;
  border-radius: 12px;
  box-shadow: 0 4px 18px rgba(0, 78, 90, 0.05);
}

.ms-page-header__content {
  display: flex;
  align-items: center;
  gap: 16px;
}

.ms-page-header__icon {
  width: 44px;
  height: 44px;
  border-radius: 10px;
  background: rgba(0, 130, 152, 0.08);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.15rem;
  color: #008298;
}

.ms-page-header__title {
  font-size: 1.45rem;
  font-weight: 800;
  color: #00363f;
  margin: 0;
  letter-spacing: -0.3px;
}

.ms-page-header__subtitle {
  font-size: 0.85rem;
  color: #557d84;
  margin: 4px 0 0;
  font-weight: 400;
}

.ms-page-header__breadcrumb {
  font-size: 0.76rem;
  color: #557d84;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.8px;
  margin-bottom: 4px;
}

.ms-breadcrumb-sep {
  margin: 0 6px;
  color: rgba(0, 130, 152, 0.3);
}

/* ── Modal Header Standard ── */
.pp-editor-modal-header {
  padding: 18px 24px;
  background: linear-gradient(135deg, #ffffff 0%, #f4f9fb 100%);
  border-bottom: 1px solid #e2e8f0;
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.pp-editor-icon {
  width: 42px;
  height: 42px;
  border-radius: 10px;
  background: #e6f7fa;
  color: #008298;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.1rem;
}

.pp-editor-title {
  margin: 0;
  font-size: 1.2rem;
  font-weight: 800;
  color: #00363f;
}

.pp-editor-subtitle {
  margin: 2px 0 0;
  font-size: 0.82rem;
  color: #64748b;
}

.pp-editor-close {
  background: none;
  border: none;
  font-size: 1.5rem;
  color: #94a3b8;
  cursor: pointer;
}

.pp-editor-close:hover {
  color: #0f172a;
}

.pp-editor-body {
  padding: 24px;
  max-height: 80vh;
  overflow-y: auto;
}

/* ── Section Cards ── */
.pp-section-card {
  background: #ffffff;
  border: 1px solid #e2e8f0;
  border-radius: 14px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.04);
  overflow: hidden;
}

.pp-section-header {
  padding: 14px 20px;
  background: #f8fafc;
  border-bottom: 1px solid #e2e8f0;
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.pp-section-title {
  margin: 0;
  font-size: 0.95rem;
  font-weight: 700;
  color: #1e293b;
}

.pp-section-subtitle {
  font-size: 0.8rem;
  color: #64748b;
  margin-top: 2px;
}

.pp-section-content {
  padding: 20px;
}

/* ── Form Controls ── */
.pp-form-label {
  font-size: 0.8rem;
  font-weight: 700;
  color: #475569;
  text-transform: uppercase;
  letter-spacing: 0.4px;
  margin-bottom: 6px;
  display: block;
}

.pp-form-label.required::after {
  content: " *";
  color: #ef4444;
}

.pp-form-input, .pp-form-select {
  width: 100%;
  padding: 9px 12px;
  border: 1.5px solid #e2e8f0;
  border-radius: 8px;
  font-size: 0.875rem;
  color: #1e293b;
  background: #ffffff;
  outline: none;
  transition: border-color 0.2s, box-shadow 0.2s;
}

.pp-form-input:focus, .pp-form-select:focus {
  border-color: #00A0C1;
  box-shadow: 0 0 0 3px rgba(0, 160, 193, 0.12);
}

.pp-form-input:disabled, .pp-form-select:disabled {
  background-color: #f8fafc !important;
  color: #475569;
  cursor: not-allowed;
  border-color: #e2e8f0;
  opacity: 0.95;
}

.pp-toggle-switch {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  cursor: pointer;
  user-select: none;
}

.pp-toggle-switch input {
  display: none;
}

.pp-toggle-slider {
  width: 44px;
  height: 24px;
  background-color: #cbd5e1;
  border-radius: 24px;
  position: relative;
  transition: background-color 0.2s;
}

.pp-toggle-slider::after {
  content: "";
  position: absolute;
  top: 2px;
  left: 2px;
  width: 20px;
  height: 20px;
  background-color: #ffffff;
  border-radius: 50%;
  transition: transform 0.2s;
  box-shadow: 0 1px 3px rgba(0,0,0,0.2);
}

.pp-toggle-switch input:checked + .pp-toggle-slider {
  background-color: #00A0C1;
}

.pp-toggle-switch input:checked + .pp-toggle-slider::after {
  transform: translateX(20px);
}

.pp-toggle-label {
  font-size: 0.875rem;
  font-weight: 600;
  color: #334155;
}

/* ── Tree Empty State ── */
.pp-tree-empty-state {
  text-align: center;
  padding: 40px 20px;
  background: #f8fafc;
  border: 1.5px dashed #cbd5e1;
  border-radius: 12px;
}

.pp-empty-icon-wrap {
  width: 60px;
  height: 60px;
  border-radius: 50%;
  background: #e2e8f0;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto;
}

.tree-root-container {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.pp-tree-summary-footer {
  padding: 14px 20px;
  background: #f8fafc;
  border-top: 1px solid #e2e8f0;
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 12px;
}

.text-teal {
  color: #008298 !important;
}

.pp-alert-danger {
  background: #fef2f2;
  border: 1px solid #fecaca;
  color: #991b1b;
  border-radius: 8px;
  padding: 12px 16px;
}

/* ── Buttons (Simple Solid Colors - Matching Spark Standards) ── */
.pp-btn {
  padding: 8px 16px;
  border-radius: 6px;
  font-size: 0.875rem;
  font-weight: 600;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  transition: background-color 0.2s ease, border-color 0.2s ease, color 0.2s ease;
  border: 1px solid transparent;
}

.pp-btn--sm {
  padding: 6px 14px;
  font-size: 0.82rem;
  border-radius: 6px;
}

.pp-btn--primary {
  background-color: #00A0C1;
  border-color: #00A0C1;
  color: #ffffff;
  box-shadow: none;
}

.pp-btn--primary:hover {
  background-color: #008298;
  border-color: #008298;
  color: #ffffff;
  transform: none;
  box-shadow: none;
}

.pp-btn--ghost {
  background-color: transparent;
  color: #008298;
  border: 1px solid #cbd5e1;
}

.pp-btn--ghost:hover {
  background-color: #f1f5f9;
  border-color: #94a3b8;
  color: #006070;
}
</style>
